/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useTextDimensions } from '../hooks/PRIVATE_useTextDimensions';
import { useCssVars } from '../hooks/PRIVATE_useCssVars/useCssVars';
import { getChartVars } from '../utils/PRIVATE_chartUtils';
import { styles } from './themes/ChartStyles.css';
import { useRef } from 'preact/hooks';
import { PlotArea } from '../UNSAFE_PlotArea';
import { useId } from '../hooks/UNSAFE_useId';
import { Axis } from '../UNSAFE_Axis/Axis';
import { TrackResizeContainer } from '../PRIVATE_TrackResizeContainer';
import { useChartViewport } from '../hooks/PRIVATE_useChartViewport';
import {
  getAvailSpace,
  getLayoutInfo,
  getScrollBarSpace,
  updateScrollBarSpace
} from '../utils/PRIVATE_chartUtils/layoutUtils';
import { useChartNav } from '../hooks/PRIVATE_useChartNav/useChartNav';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { getAxesPosition } from '../UNSAFE_Axis/utils/axisUtils';
import { ScrollBar } from '../PRIVATE_ChartScrollBar';
import { useSelectDrill } from '../hooks/PRIVATE_useChartEvents/useSelectDrill';
import { useChartDatatipAndCursor } from '../hooks/PRIVATE_useChartEvents/useDatatipAndCursor';
import { getGroupNavUtil } from '../UNSAFE_Axis/utils/axisNavUtils';
import { ChartCommonProps, ChartItem } from './chart.types';
import { useTestId } from '../hooks/UNSAFE_useTestId';
import { TimeAxisProps } from '../UNSAFE_Axis/axis.types';
import { Gridlines } from '../UNSAFE_PlotArea/Gridlines';

export function Chart<K extends string | number, D extends ChartItem<K>>({
  width = '100%',
  height = '448px',
  selectedIds = [],
  hiddenIds,
  hideAndShowBehavior = 'none',
  orientation = 'vertical',
  xAxis,
  yAxis,
  plotArea,
  series,
  groups,
  isStacked = false,
  getDataItem,
  selectionMode = 'none',
  drilling = 'off',
  dataCursorPosition,
  onSelectionChange,
  isDataCursorEnabled,
  dataCursorStyle,
  onViewportChange,
  onDataCursorPositionChange,
  onItemDrill,
  onGroupDrill,
  onItemInput,
  datatip,
  zoomAndScroll = 'off',
  offset,
  getNavUtil,
  getDataItemPos,
  getGapRatio,
  valueFormats,
  findNearest,
  isRtl,
  children,
  testId,
  renderGridLinesInFront,
  ...props
}: ChartCommonProps<K, D>) {
  // TODO: reevaluate how we handle empty data
  const chartVars = getChartVars();

  const testIdProps = useTestId(testId);
  return (
    <TrackResizeContainer width={width} height={height}>
      {function _(width, height) {
        const rootRef = useRef<HTMLDivElement>(null);
        const isHoriz = orientation === 'horizontal';
        const { xAxisPosition, yAxisPosition } = getAxesPosition(yAxis, isHoriz, isRtl);

        const { resolvedVars, cssContent } = useCssVars(chartVars);
        const { textMeasureContent, getTextDimensions } = useTextDimensions();

        let availSpace = getAvailSpace(width, height);
        const updatedSpace = getScrollBarSpace(availSpace, zoomAndScroll, xAxisPosition);
        let scrollDims = updatedSpace.scrollDims;
        availSpace = updatedSpace.availSpace;

        const hiddenSet = new Set(hiddenIds);
        const {
          xProps,
          xScale,
          xViewportExtent,
          xAxisExtent,
          xIndexExtent,
          yViewportExtent,
          yAxisExtent,
          groupWidth,
          yProps,
          yScale,
          yMaxDim,
          yMajorTicks,
          yMinorTicks,
          plotAreaSpace
        } = getLayoutInfo(
          series,
          groups,
          getDataItem,
          xAxis,
          yAxis,
          availSpace,
          hiddenSet,
          hideAndShowBehavior,
          isHoriz,
          isRtl,
          isStacked,
          resolvedVars['labelFontSize'],
          offset,
          getTextDimensions
        );

        scrollDims = updateScrollBarSpace(scrollDims, xAxisPosition === 'bottom', yMaxDim);

        const gapRatio = getGapRatio(xIndexExtent.startIndex, xIndexExtent.endIndex);

        const getNextChartItem = getNavUtil(xIndexExtent.startIndex, xIndexExtent.endIndex);

        const renderedGroupsIndex = xProps.tickLabels.map(
          ({ index }) => index + xIndexExtent.startIndex
        );
        const { getNextNavGroup, getDefaultNavGroup } = getGroupNavUtil(
          groups,
          renderedGroupsIndex,
          xIndexExtent,
          drilling,
          isRtl
        );

        const { activeId, focusedItemInfo, hoveredItemInfo, focusedGroupInfo, navProps } =
          useChartNav({
            rootRef,
            getNextChartItem,
            getNextNavGroup,
            getDefaultNavGroup,
            onItemInput,
            drilling,
            xIndexExtent
          });

        const selectDrillProps = useSelectDrill({
          selectionMode,
          selection: selectedIds,
          onChange: onSelectionChange,
          rootRef,
          drilling,
          onItemDrill,
          onGroupDrill,
          focusedGroupInfo,
          focusedItemInfo,
          groups,
          getDataItem
        });

        const viewportProps = useChartViewport({
          width,
          height,
          xViewportExtent,
          yViewportExtent,
          xAxisExtent,
          yAxisExtent,
          xScale,
          yScale,
          plotAreaSpace,
          isRtl,
          isHoriz,
          zoomAndScroll,
          onViewportChange
        });

        const findNearestData = findNearest(xScale as any, yScale, groupWidth, gapRatio);
        const getDataItemPosition = getDataItemPos(xScale as any, yScale, groupWidth, gapRatio);

        const { datatipContent, dataCursorContent, datatipProps } = useChartDatatipAndCursor<K, D>({
          series,
          groups,
          width,
          height,
          findNearest: findNearestData,
          plotAreaSpace,
          onDataCursorPositionChange,
          isDataCursorEnabled,
          dataCursorPosition,
          dataCursorStyle,
          hoveredItemInfo,
          focusedItemInfo,
          getDataItem,
          getDataItemPos: getDataItemPosition,
          datatip,
          valueFormats,
          isHoriz,
          xScale: xScale as any,
          yScale
        });

        const eventsProps = mergeProps(navProps, selectDrillProps, viewportProps, datatipProps);

        const isGroupInteractive = (index: number) => {
          if ((xAxis as TimeAxisProps)?.timeAxisType != null) {
            return false;
          }
          const groupDrill = groups[index].drilling;
          if (groupDrill === 'on') {
            return true;
          }

          if (groupDrill != 'off' && (drilling === 'on' || drilling === 'groupsOnly')) {
            return true;
          }
          return false;
        };

        const plotAreaClipPathId = useId();
        const { viewportMin: xViewportMin, viewportMax: xViewportMax } = xViewportExtent;
        const { startIndex: xStartIndex, endIndex: xEndIndex } = xIndexExtent;
        const gridLines = (
          <Gridlines
            plotArea={plotArea}
            yMinorTicks={yMinorTicks}
            xScale={xScale}
            yScale={yScale}
            isLog={yAxis?.scale === 'log'}
            xViewportMin={xViewportMin}
            xViewportMax={xViewportMax}
            yTicks={yMajorTicks}
            yAxisPosition={yAxisPosition}
          />
        );

        return (
          <div
            tabIndex={0}
            ref={rootRef}
            aria-activedescendant={activeId}
            aria-label={props['aria-label']}
            aria-labelledby={props['aria-labelledby']}
            aria-describedby={props['aria-describedby']}
            style={{ width, height }}
            role="application"
            className={styles.outline}
            {...testIdProps}
            {...eventsProps}>
            <svg width="100%" height="100%">
              <clipPath id={`${plotAreaClipPathId}`}>
                <rect {...plotAreaSpace}></rect>
              </clipPath>
              <PlotArea
                color={plotArea?.color}
                yScale={yScale}
                isLog={yAxis?.scale === 'log'}
                availSpace={plotAreaSpace}
                xAxisPosition={xAxisPosition}
              />
              {!renderGridLinesInFront && gridLines}
              <Axis {...yProps} axisPosition={yAxisPosition} />
              <Axis
                {...xProps}
                axisPosition={xAxisPosition}
                isTextInteractive={isGroupInteractive}
                activeId={activeId}
                focusedGroupIndex={
                  focusedGroupInfo.isFocusVisible ? focusedGroupInfo.groupIndex : undefined
                }
              />
              <g clip-path={`url(#${plotAreaClipPathId})`}>
                {children({
                  xStartIndex,
                  xEndIndex,
                  xScale: xScale as any,
                  yScale,
                  gapRatio,
                  activeId,
                  focusedItemInfo,
                  hoveredItemInfo,
                  groupWidth
                })}
                {renderGridLinesInFront && gridLines}
                {dataCursorContent}
              </g>
              {zoomAndScroll === 'live' && (
                <ScrollBar
                  availSpace={scrollDims}
                  viewport={xViewportExtent}
                  axisExtent={xAxisExtent}
                  isHoriz={xAxisPosition === 'bottom'}
                />
              )}
            </svg>
            {textMeasureContent}
            {cssContent}
            {datatipContent}
          </div>
        );
      }}
    </TrackResizeContainer>
  );
}
